<template>
	<QuotePageAnchorMenu v-if="!isXl" />

	<section
		class="bg-[#EFF6FF] relative pt-4 pb-8 lg:py-6 xl:py-4 xxl:py-9 flex flex-col items-center"
		id="quote-page"
	>
		<QuotePageAnchorMenu v-if="isXl" />
		<Container
			id="quote-page-content"
			class="grid grid-cols-1 lg:grid-cols-12 lg:items-start gap-4 !px-4 lg:px-0"
		>
			<!-- Loading State -->
			<div
				v-if="!stock_data && isLoading"
				class="col-span-12 flex justify-center items-center py-20"
			>
				<div class="text-center">
					<div
						class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"
					></div>
					<p class="text-slate-600">Loading stock information...</p>
				</div>
			</div>

			<!-- Main Content -->
			<!-- <template v-else-if="stock_data"> -->
			<template v-else-if="stock_data">
				<div class="lg:col-span-9 flex flex-col lg:block quote-body">
					<!-- Stock Header -->
					<div class="lg:bg-white rounded-2xl lg:p-8">
						<div
							id="summary"
							class="flex flex-col items-start gap-2 lg:gap-5 mb-3 lg:mb-8"
						>
							<div class="flex flex-col gap-1 w-full">
								<div class="flex items-center gap-2 justify-between w-full">
									<h1 class="font-bold text-4xl">{{ stock_data.SYMBOL }}</h1>

									<NuxtLink
										:href="aiBreakthroughUrl"
										class="hidden lg:flex text-blue-300 text-sm underline items-center gap-2"
										:target="'_blank'"
									>
										<svg
											width="22"
											height="23"
											viewBox="0 0 22 23"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M11 0.5C4.92487 0.5 0 5.42487 0 11.5C0 11.5992 0 11.6488 0.010142 11.7195C0.0555927 12.0362 0.306388 12.3656 0.599555 12.4938C0.664973 12.5223 0.722868 12.5383 0.838656 12.5703C1.02017 12.6204 1.18381 12.6643 1.32042 12.6999C2.18106 12.8961 3.07777 13 3.99996 13C7.30564 13 10.2993 11.664 12.4706 9.5H11C10.4477 9.5 10 9.05228 10 8.5C10 7.94772 10.4477 7.5 11 7.5H15C15.5523 7.5 16 7.94772 16 8.5V12.5C16 13.0523 15.5523 13.5 15 13.5C14.4477 13.5 14 13.0523 14 12.5V10.7978C11.4598 13.3899 7.91796 15 3.99996 15C3.73986 15 3.48086 14.9931 3.22292 14.9791C2.21076 14.9244 1.70468 14.8971 1.42612 15.0856C1.18892 15.2461 1.02431 15.5187 0.992674 15.8033C0.955525 16.1376 1.15315 16.4686 1.54839 17.1307C3.46824 20.3464 6.98262 22.5 11 22.5C17.0751 22.5 22 17.5751 22 11.5C22 5.42487 17.0751 0.5 11 0.5Z"
												fill="#4383FF"
											/>
										</svg>

										Time to buy {{ stock_data.SYMBOL }}?
									</NuxtLink>
								</div>

								<div class="flex justify-start gap-2 items-center">
									<span
										class="text-sm lg:text-xl text-slate-300 uppercase max-w-[240px] lg:max-w-[360px] text-ellipsis overflow-hidden whitespace-nowrap"
									>
										{{
											stock_data.NAME &&
											stock_data.NAME !== "" &&
											stock_data.NAME !== "null"
												? stock_data.NAME
												: stock_data.SYMBOL
										}}
									</span>

									<template v-if="company_data">
										<span class="block w-[1px] h-[20px] bg-slate-300" />

										<span class="text-sm lg:text-xl text-slate-300 uppercase">
											{{ normalizedExchange }}
										</span>
									</template>

									<template v-else>
										<span class="block w-[1px] h-[20px] bg-slate-300" />

										<span
											class="w-[64px] h-[20px] bg-slate-100/[0.25] rounded-md animate-pulse"
										/>
									</template>
								</div>
							</div>

							<span class="border-b border-slate-100 w-full h-[1px] hidden lg:block" />
							<div class="flex items-center gap-3 w-full lg:flex-row">
								<div class="flex flex-row items-start py-3 lg:py-0 w-full lg:w-auto">
									<div class="flex flex-col lg:gap-3">
										<h2 class="font-bold text-4xl">
											${{
												isAmericanExchange
													? stock_data_realtime.LAST !== null
														? formatPrice(stock_data_realtime.LAST)
														: "N/A"
													: stock_data_realtime.CLOSE !== null
													? formatPrice(stock_data_realtime.CLOSE)
													: "N/A"
											}}
										</h2>

										<span
											class="rounded-[8px] px-3 py-1 text-base font-medium lg:hidden"
											:class="
												(stock_data_realtime.NET_CHG || 0) >= 0
													? '  bg-success/[0.2] text-success'
													: 'bg-red-500/[0.2] text-red-500'
											"
										>
											{{ (stock_data_realtime.NET_CHG || 0) >= 0 ? "+" : "" }}${{
												formatPriceChange(stock_data_realtime.NET_CHG || 0)
											}}
											({{ formatPercentageChange(stock_data_realtime.PC_CHG || 0) }}%)
										</span>
									</div>

									<NuxtLink
										:href="aiBreakthroughUrl"
										class="flex lg:!hidden text-blue-300 text-sm underline items-center gap-2 ml-auto"
										:target="'_blank'"
									>
										<svg
											width="22"
											height="23"
											viewBox="0 0 22 23"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M11 0.5C4.92487 0.5 0 5.42487 0 11.5C0 11.5992 0 11.6488 0.010142 11.7195C0.0555927 12.0362 0.306388 12.3656 0.599555 12.4938C0.664973 12.5223 0.722868 12.5383 0.838656 12.5703C1.02017 12.6204 1.18381 12.6643 1.32042 12.6999C2.18106 12.8961 3.07777 13 3.99996 13C7.30564 13 10.2993 11.664 12.4706 9.5H11C10.4477 9.5 10 9.05228 10 8.5C10 7.94772 10.4477 7.5 11 7.5H15C15.5523 7.5 16 7.94772 16 8.5V12.5C16 13.0523 15.5523 13.5 15 13.5C14.4477 13.5 14 13.0523 14 12.5V10.7978C11.4598 13.3899 7.91796 15 3.99996 15C3.73986 15 3.48086 14.9931 3.22292 14.9791C2.21076 14.9244 1.70468 14.8971 1.42612 15.0856C1.18892 15.2461 1.02431 15.5187 0.992674 15.8033C0.955525 16.1376 1.15315 16.4686 1.54839 17.1307C3.46824 20.3464 6.98262 22.5 11 22.5C17.0751 22.5 22 17.5751 22 11.5C22 5.42487 17.0751 0.5 11 0.5Z"
												fill="#4383FF"
											/>
										</svg>

										Time to buy {{ stock_data_realtime.SYMBOL }}?
									</NuxtLink>
								</div>

								<span
									class="rounded-[8px] px-3 py-1 text-base font-medium lg:inline-block hidden"
									:class="
										stock_data_realtime.NET_CHG >= 0
											? '  bg-success/[0.2] text-success'
											: 'bg-red-500/[0.2] text-red-500'
									"
								>
									{{ stock_data_realtime.NET_CHG >= 0 ? "+" : "" }}${{
										formatPriceChange(stock_data_realtime.NET_CHG)
									}}
									({{ formatPercentageChange(stock_data_realtime.PC_CHG) }}%)
								</span>

								<span
									class="text-slate-400 text-sm hidden lg:inline"
									v-if="isClient && stock_data_realtime.LAST_TRADE_TIME"
								>
									{{ daysAgoStock(stock_data_realtime.LAST_TRADE_TIME, true) }}
								</span>
								<span
									v-else
									class="h-[16px] w-[72px] animate-pulse bg-slate-300/[0.1] rounded-sm"
								/>
							</div>

							<span
								class="text-slate-400 text-xs lg:text-sm"
								v-if="isClient && stock_data_realtime.LAST_TRADE_TIME"
							>
								Last updated {{ formattedLastTradeTime }}
							</span>
							<span
								v-else
								class="h-[16px] w-[220px] animate-pulse bg-slate-300/[0.1] rounded-sm"
							/>

							<div class="flex items-center gap-2">
								<svg
									class="lg:h-5 lg:w-5 h-4 w-4"
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M12 2C6.48867 2 2 6.48867 2 12C2 17.5113 6.48867 22 12 22C14.2468 22 16.3284 21.2553 18 20L20 22V17H15L16.5625 18.5625C15.2683 19.4646 13.702 20 12 20C7.56933 20 4 16.4307 4 12C4 7.56933 7.56933 4 12 4C16.4307 4 20 7.56933 20 12H22C22 6.48867 17.5113 2 12 2ZM11 6V13H16V11H13V6H11Z"
										fill="#868686"
									/>
								</svg>

								<span class="text-slate-400 text-xs lg:text-sm">Prices delayed.</span>

								<NuxtLink
									:href="realtimeQuotesUrl"
									class="text-blue-300 text-xs lg:text-sm underline"
									:target="'_blank'"
								>
									Get real-time quotes here!
								</NuxtLink>
							</div>
						</div>

						<!-- Chart.js Component -->
						<div class="inline-flex flex-wrap flex-col gap-4">
							<div class="lg:flex justify-between w-full gap-3">
								<div class="chart-controls !mb-0">
									<button
										v-for="chartType in ['Candlestick', 'Line Chart']"
										:key="chartType"
										:class="[
											'px-3 py-1 min-w-[32px] rounded-sm font-medium text-slate-600 flex items-center justify-center gap-2 lg:min-w-[88px]',
											selectedChartType === chartType
												? 'bg-white text-black'
												: 'text-slate-400'
										]"
										@click="setChartType(chartType)"
									>
										<!-- Area Chart Icon -->
										<svg
											v-if="chartType === 'Line Chart'"
											width="18"
											height="18"
											viewBox="0 0 18 18"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M1.67023 1.12181C1.52162 1.12443 1.38009 1.18575 1.27655 1.29238C1.17301 1.39901 1.11587 1.54225 1.11762 1.69087V16.3148C1.11704 16.464 1.17575 16.6073 1.28083 16.7132C1.3859 16.8191 1.52874 16.8789 1.67792 16.8795H16.3073C16.3822 16.8812 16.4567 16.8678 16.5264 16.8403C16.5961 16.8128 16.6596 16.7717 16.7132 16.7193C16.7668 16.6669 16.8093 16.6043 16.8384 16.5352C16.8675 16.4661 16.8824 16.3919 16.8824 16.317C16.8824 16.2421 16.8675 16.1679 16.8384 16.0988C16.8093 16.0297 16.7668 15.9671 16.7132 15.9147C16.6596 15.8624 16.5961 15.8212 16.5264 15.7937C16.4567 15.7662 16.3822 15.7528 16.3073 15.7545H2.24371V1.69087C2.2446 1.61552 2.23034 1.54079 2.20178 1.47106C2.17322 1.40132 2.13094 1.338 2.07745 1.28492C2.02396 1.23184 1.96035 1.19006 1.89039 1.16204C1.82044 1.13401 1.74557 1.12034 1.67023 1.12181ZM16.2941 4.77915C16.1484 4.78379 16.0101 4.84487 15.9085 4.94946L10.1363 10.7217L7.7204 8.06409C7.62003 7.95419 7.48019 7.88853 7.33152 7.88152C7.18285 7.8745 7.03746 7.92669 6.92719 8.02665L3.55109 11.1204C3.49654 11.1703 3.45236 11.2305 3.42107 11.2975C3.38977 11.3645 3.37198 11.437 3.36871 11.5108C3.36544 11.5847 3.37675 11.6585 3.402 11.728C3.42725 11.7975 3.46594 11.8613 3.51587 11.9159C3.56579 11.9704 3.62597 12.0146 3.69296 12.0459C3.75996 12.0772 3.83246 12.0949 3.90633 12.0982C3.9802 12.1014 4.05399 12.0901 4.12348 12.0648C4.19297 12.0396 4.25681 12.0009 4.31134 11.951L7.27216 9.23952L9.70123 11.9136C9.7523 11.9699 9.81425 12.0153 9.88333 12.047C9.95241 12.0787 10.0272 12.096 10.1032 12.098C10.1792 12.1 10.2548 12.0866 10.3254 12.0585C10.3961 12.0305 10.4603 11.9884 10.5142 11.9349L16.7017 5.74411C16.7821 5.66503 16.8369 5.5636 16.859 5.453C16.881 5.34239 16.8693 5.22773 16.8254 5.12386C16.7814 5.01999 16.7073 4.93171 16.6126 4.87051C16.5178 4.8093 16.4069 4.77652 16.2941 4.77915Z"
												:fill="selectedChartType === chartType ? '#000000' : '#666D80'"
											/>
										</svg>

										<!-- Candlestick Chart Icon -->
										<svg
											v-else-if="chartType === 'Candlestick'"
											width="18"
											height="18"
											viewBox="0 0 18 18"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M2.8125 11.25H3.375V12.9375C3.375 13.248 3.62644 13.5 3.9375 13.5C4.24856 13.5 4.5 13.248 4.5 12.9375V11.25H5.0625C5.37356 11.25 5.625 10.998 5.625 10.6875V5.0625C5.625 4.752 5.37356 4.5 5.0625 4.5H4.5V2.8125C4.5 2.502 4.24856 2.25 3.9375 2.25C3.62644 2.25 3.375 2.502 3.375 2.8125V4.5H2.8125C2.50144 4.5 2.25 4.752 2.25 5.0625V10.6875C2.25 10.998 2.50144 11.25 2.8125 11.25ZM3.375 5.625H4.5V10.125H3.375V5.625Z"
												:fill="selectedChartType === chartType ? '#000000' : '#666D80'"
											/>
											<path
												d="M7.875 14.625H8.4375V16.3125C8.4375 16.623 8.68894 16.875 9 16.875C9.31106 16.875 9.5625 16.623 9.5625 16.3125V14.625H10.125C10.4361 14.625 10.6875 14.373 10.6875 14.0625V10.125C10.6875 9.8145 10.4361 9.5625 10.125 9.5625H9.5625V7.875C9.5625 7.5645 9.31106 7.3125 9 7.3125C8.68894 7.3125 8.4375 7.5645 8.4375 7.875V9.5625H7.875C7.56394 9.5625 7.3125 9.8145 7.3125 10.125V14.0625C7.3125 14.373 7.56394 14.625 7.875 14.625ZM8.4375 10.6875H9.5625V13.5H8.4375V10.6875Z"
												:fill="selectedChartType === chartType ? '#000000' : '#666D80'"
											/>
											<path
												d="M12.9375 12.375H13.5V14.0625C13.5 14.373 13.7514 14.625 14.0625 14.625C14.3736 14.625 14.625 14.373 14.625 14.0625V12.375H15.1875C15.4986 12.375 15.75 12.123 15.75 11.8125V3.9375C15.75 3.627 15.4986 3.375 15.1875 3.375H14.625V1.6875C14.625 1.377 14.3736 1.125 14.0625 1.125C13.7514 1.125 13.5 1.377 13.5 1.6875V3.375H12.9375C12.6264 3.375 12.375 3.627 12.375 3.9375V11.8125C12.375 12.123 12.6264 12.375 12.9375 12.375ZM13.5 4.5H14.625V11.25H13.5V4.5Z"
												:fill="selectedChartType === chartType ? '#000000' : '#666D80'"
											/>
										</svg>

										<span
											class="text-sm inline"
											:class="[
												selectedChartType === chartType ? 'text-black' : 'text-slate-400'
											]"
										>
											{{ chartType.charAt(0).toUpperCase() + chartType.slice(1) }}
										</span>
									</button>
								</div>

								<div
									v-if="previousTradingDayNotice"
									class="ml-auto rounded-lg hidden lg:block bg-slate-100/[.50] px-3 py-2 text-sm text-slate-600"
								>
									{{ previousTradingDayNotice }}
								</div>
							</div>
							<div class="chart-controls">
								<button
									v-for="range in ['1D', '5D', '1M', '6M', '1Y', '5Y', 'All']"
									:key="range"
									:class="[
										'px-3 py-1 rounded-sm font-medium',
										selectedRange === range ? 'bg-white text-black' : 'text-slate-400'
									]"
									@click="setRange(range)"
								>
									{{ range }}
								</button>
							</div>
						</div>

						<div class="relative">
							<div
								ref="chartContainer"
								class="w-full"
								@scroll="handleChartScroll"
							>
								<div class="h-[420px] relative lg:min-w-[680px] md:w-full">
									<template v-if="chartData">
										<ClientOnly>
											<HighchartsStockChart
												:data="chartData"
												:chartType="selectedChartType"
												:selectedRange="selectedRange"
											/>
										</ClientOnly>
									</template>

									<div
										v-else
										class="h-full w-full bg-slate-300/[0.1] animate-pulse rounded-2xl"
									/>
								</div>
							</div>
						</div>
					</div>

					<div
						class="bg-white rounded-2xl p-6 w-full lg:max-w-xs lg:hidden relative border border-slate-100"
						:class="[
							'transition-all duration-300 ease-in-out',
							isOverviewExpanded ? ' h-[520px]' : 'h-[264px] overflow-hidden'
						]"
						v-if="current_stock_data"
					>
						<h2 class="text-2xl font-semibold mb-4 text-black">Overview</h2>
						<div class="relative">
							<div class="space-y-4">
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Day's Range</span>
									<span class="font-semibold text-slate-900 text-right">
										${{ formatPrice(current_stock_data.LOW) }} – ${{
											formatPrice(current_stock_data.HIGH)
										}}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">52 Week Range</span>
									<span class="font-semibold text-slate-900 text-right">
										${{ formatPrice(current_stock_data.LOW_52W) }} – ${{
											formatPrice(current_stock_data.HIGH_52W)
										}}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Market Cap</span>
									<span class="font-semibold text-slate-900 text-right">
										{{ formatNumber(current_stock_data.MARKET_CAP || 0, "volume") }}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Volume</span>
									<span class="font-semibold text-slate-900 text-right">
										{{ formatNumber(current_stock_data.VOLUME || 0, "volume") }}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Beta</span>
									<span class="font-semibold text-slate-900 text-right">
										{{
											formatNumber(
												(company_data as any)?.rawData?.company?.[0]?.shareinfo?.priceinfo
													?.beta,
												"decimal"
											)
										}}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">EPS</span>
									<span class="font-semibold text-slate-900 text-right">
										{{ formatNumber((company_data as any)?.eps, "currency") }}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Analyst Rating</span>
									<span
										:class="[
											'text-white px-4 py-1 rounded-md font-regular text-base text-white',
											{
												'bg-[#12B76A]': ['Strong Buy', 'Buy'].includes(analystRatingLabel),
												'bg-[#F79009]': ['Hold'].includes(analystRatingLabel),
												'bg-[##F04438]': ['Sell', 'N/A'].includes(analystRatingLabel)
											}
										]"
									>
										{{ analystRatingLabel }}
									</span>
								</div>
							</div>

							<!-- Gradient fade overlay when collapsed -->
							<div
								v-if="!isOverviewExpanded"
								class="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none"
							></div>
						</div>

						<!-- Read More button -->

						<ShowMore
							:is-expanded="isOverviewExpanded"
							@click="toggleOverviewExpansion"
						/>
					</div>
					<div
						v-else
						class="h-[264px] bg-slate-300/[0.1] animate-pulse rounded-2xl lg:hidden"
					/>

					<StockAlertCard class="lg:hidden" />

					<template v-if="stock_data?.SYMBOL">
						<TradingPerspectiveWidget :symbol="stock_data?.SYMBOL" />
					</template>
					<div v-if="isLoadingNews">
						<NewsAnalysisGridSkeleton />
					</div>

					<NewsAnalysisGrid
						v-else-if="!isLoadingNews && related_news.length > 0"
						:news="related_news"
						id="news"
					/>

					<TopGainerStocks
						v-if="!isDesktop"
						id="top-gainers-header"
						is-quote-page
					/>

					<FinancialWealth
						v-if="enhanced_financials_data || financial_data"
						id="financials"
						:pe-ratio="financial_data?.pe_ratio"
						:earnings-date="financial_data?.earnings_date"
						:eps="financial_data?.eps"
						:revenue="financial_data?.revenue"
						:dividend-yield="financial_data?.dividend_yield"
						:profit-margin="financial_data?.profit_margin"
						:enhanced-financials="enhanced_financials_data"
					/>

					<AnalystRatings
						id="analyst-rating"
						:rating-label="analystRatingLabel"
						:price-target="analystPriceTarget"
						:total-ratings="analystTotalRatings"
						:sell="analystSellCount"
						:hold="analystHoldCount"
						:buy="analystBuyCount"
					/>

					<StockEarnings
						id="earning"
						:symbol="stock_data.SYMBOL"
						:name="stock_data.NAME"
						:earnings-data="earnings_data"
						:earnings-dates="earnings_dates_data"
					/>

					<ClientOnly>
						<StockFaq
							id="faqs"
							:symbol="stock_data.SYMBOL"
							:analyst-rating-label="analystRatingLabel"
							:dividend-yield="
								financial_data && financial_data.dividend_yield
									? financial_data.dividend_yield
									: 0
							"
							:pe-ratio="
								financial_data && financial_data.pe_ratio ? financial_data.pe_ratio : 0
							"
							:dividends-data="dividends_data ?? {}"
							:faq-data="stock_earnings_faq_data ?? {}"
							:name="stock_data.NAME"
							:next-earnings-date="earnings_dates_data?.event[0]?.date"
						/>

						<StockPickSuggestions
							:sector="stock_sector"
							:price="stock_data?.BID || 0"
						/>
					</ClientOnly>
				</div>

				<div
					class="lg:col-span-3 flex flex-col gap-6"
					v-if="stock_data_realtime && isMini"
				>
					<div class="bg-white rounded-2xl p-6 w-full lg:max-w-xs">
						<h2 class="text-2xl font-semibold mb-4 text-black">Overview</h2>
						<div class="relative">
							<div class="space-y-4">
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Day's Range</span>
									<span class="font-semibold text-slate-900 text-right">
										${{ formatPrice(stock_data_realtime.LOW) }} – ${{
											formatPrice(stock_data_realtime.HIGH)
										}}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">52 Week Range</span>
									<span class="font-semibold text-slate-900 text-right">
										${{ formatPrice(stock_data_realtime.LOW_52W) }} – ${{
											formatPrice(stock_data_realtime.HIGH_52W)
										}}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Market Cap</span>
									<span class="font-semibold text-slate-900 text-right">
										{{ formatNumber(stock_data_realtime.MARKET_CAP, "volume") }}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Volume</span>
									<span class="font-semibold text-slate-900 text-right">
										{{ formatNumber(stock_data_realtime.VOLUME, "volume") }}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">Beta</span>
									<span class="font-semibold text-slate-900 text-right">
										{{
											formatNumber(
												(company_data as any)?.rawData?.company?.[0]?.shareinfo?.priceinfo
													?.beta,
												"decimal"
											)
										}}
									</span>
								</div>
								<hr class="my-2 border-slate-100" />
								<div class="flex justify-between items-center text-sm">
									<span class="text-slate-400">EPS</span>
									<span class="font-semibold text-slate-900 text-right">
										{{ formatNumber((company_data as any)?.eps, "currency") }}
									</span>
								</div>

								<template v-if="analystRatingLabel !== 'N/A'">
									<hr class="my-2 border-slate-100" />
									<div class="flex justify-between items-center text-sm">
										<span class="text-slate-400">Analyst Rating</span>
										<span
											:class="[
												'text-white px-4 py-1 rounded-md font-regular text-base text-white',
												{
													'bg-[#12B76A]': ['Strong Buy', 'Buy'].includes(analystRatingLabel),
													'bg-[#F79009]': ['Hold'].includes(analystRatingLabel),
													'bg-[##F04438]': ['Sell', 'N/A'].includes(analystRatingLabel)
												}
											]"
										>
											{{ analystRatingLabel }}
										</span>
									</div>
								</template>
							</div>
						</div>
					</div>

					<QuoteFinder
						headerClass="hidden lg:flex bg-white border-none"
						:is-in-header="false"
						:is-icon="false"
						:is-in-quote-page="true"
						:disable-preview="true"
					/>

					<TopGainerStocks
						id="top-gainers-header"
						is-quote-page
					/>
				</div>
			</template>

			<template v-else>
				<div class="col-span-12 flex flex-col items-center text-center py-16 gap-6">
					<div class="space-y-2 max-w-xl">
						<p class="text-2xl font-semibold text-slate-900">
							We’re having trouble loading {{ fallbackDisplaySymbol }}
						</p>
						<p class="text-base text-slate-600">
							{{ stockDataErrorMessage }}
						</p>
					</div>

					<button
						class="px-6 py-2 rounded-lg bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors duration-150"
						type="button"
						@click="retryInitialFetch"
					>
						Try again
					</button>
				</div>

				<div
					class="col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4 w-full"
					aria-hidden="true"
				>
					<div
						v-for="n in 4"
						:key="`quote-skeleton-${n}`"
						class="rounded-2xl border border-slate-100 bg-white p-6 animate-pulse space-y-4"
					>
						<div class="h-6 bg-slate-200 rounded w-2/3"></div>
						<div class="h-4 bg-slate-100 rounded w-full"></div>
						<div class="flex gap-4">
							<span class="h-4 bg-slate-100 rounded w-1/3"></span>
							<span class="h-4 bg-slate-100 rounded w-1/4"></span>
						</div>
					</div>
				</div>
			</template>
		</Container>
	</section>
</template>

<script lang="ts" setup>
import "chartjs-adapter-date-fns";
import {
	LineController,
	BarController,
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	BarElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	TimeScale,
	Filler
} from "chart.js";

import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import axios from "axios";
import { createError, setResponseHeader } from "h3";
import StockAlertCard from "@/components/markets/StockAlertCard.vue";
import type { StockEarningsFAQResponse } from "~/types/stocks-data";
import HighchartsStockChart from "@/components/HighchartsStockChart.vue";
import QuoteFinder from "@/components/markets/QuoteFinder.vue";
import {
	normalizeExchange,
	getExchangeDisplayName
} from "~/utils/exchange-normalizer";
import {
	getSongbirdOgImageUrl,
	getTsOgImageS3Url
} from "~/utils/songbird-client";
import { useStocksStore } from "~/stores/stocks";

const aiBreakthroughUrl = useCampaignUrl(
	"https://theaibreakthrough.com/?utm_medium=ts.com&utm_campaign=3AgLlj"
);
const realtimeQuotesUrl = useCampaignUrl(
	"https://cfn.stockstotrade.com/try-stt1?utm_campaign=1aSVn2"
);

// Type definitions
interface MostUpToDateDataType {
	BID: number | null;
	NAME: string;
	SYMBOL: string;
	TIME: string | null;
	NET_CHG: number;
	PC_CHG: number;
	HIGH_52W: number | null;
	LOW_52W: number | null;
	EPS: number | null;
	PE_RATIO: number | null;
	HIGH: number | null;
	LOW: number | null;
	CLOSE: number | null;
	LAST_TRADE_TIME: string | null;
	MARKET_CAP?: number;
	VOLUME?: number;
	MARKET?: string;
}

interface HistoricDataType {
	date: string;
	time?: string;
	open: number;
	high: number;
	low: number;
	close: number;
	volume: number;
}

interface HistoricRangeMeta {
	calendarTradingDate?: string | null;
	requestedTradingDate?: string | null;
	displayTradingDate?: string | null;
	usedPreviousTradingDay?: boolean;
}

// Computed property to determine if stock price is below $1.00
const isStockBelowOneDollar = computed(() => {
	if (!stock_data_realtime.value) {
		return false;
	}

	// Determine if it's an American exchange to choose the right price field
	const isAmerican =
		stock_data &&
		stock_data.MARKET &&
		(stock_data.MARKET === "NYSE" ||
			stock_data.MARKET === "NYE" ||
			stock_data.MARKET === "NASDAQ" ||
			stock_data.MARKET === "AMEX" ||
			stock_data.MARKET === "AMEX-ARCA" ||
			stock_data.MARKET === "AMEX-ARCA-ARCA" ||
			stock_data.MARKET === "NASDAQ-NMS" ||
			stock_data.MARKET === "NASDAQ-NMS-ARCA" ||
			stock_data.MARKET === "NGS" ||
			stock_data.MARKET === "NSC");

	const stockPrice = isAmerican
		? stock_data_realtime.value.LAST
		: stock_data_realtime.value.CLOSE;

	if (stockPrice === null || stockPrice === undefined || isNaN(stockPrice)) {
		return false;
	}

	return stockPrice < 1.0;
});

// Utility function to format stock prices
// For stocks below $1.00, show 4 decimals; otherwise show 2 decimals
const formatPrice = (value: number | null | undefined): string => {
	if (value === null || value === undefined || isNaN(value)) {
		return "N/A";
	}

	if (isStockBelowOneDollar.value) {
		return value.toFixed(4);
	}
	return value.toFixed(2);
};

// Utility function to format price change (NET_CHG)
// For stocks below $1.00, show 4 decimals; otherwise show 2 decimals
const formatPriceChange = (value: number | null | undefined): string => {
	if (value === null || value === undefined || isNaN(value)) {
		return "N/A";
	}

	if (isStockBelowOneDollar.value) {
		return value.toFixed(4);
	}
	return value.toFixed(2);
};

// Utility function to format percentage change (PC_CHG)
// Always show 2 decimals for percentage change
const formatPercentageChange = (value: number | null | undefined): string => {
	if (value === null || value === undefined || isNaN(value)) {
		return "N/A";
	}

	return value.toFixed(2);
};

// Utility function to format numbers like in the image
const formatNumber = (
	value: number,
	type: "currency" | "volume" | "decimal" = "decimal"
) => {
	if (!value || isNaN(value)) {
		return "N/A";
	}

	switch (type) {
		case "currency":
			// Format as currency - use formatPrice for stock prices
			if (value < 0.1) {
				return `$${value.toFixed(4)}`;
			}
			return `$${value.toFixed(2)}`;
		case "volume":
			// Format volume with M for millions, B for billions
			if (value >= 1000000000) {
				return `${(value / 1000000000).toFixed(2)}B`;
			} else if (value >= 1000000) {
				return `${(value / 1000000).toFixed(2)}M`;
			} else if (value >= 1000) {
				return `${(value / 1000).toFixed(2)}K`;
			}
			return value.toLocaleString();
		case "decimal":
			// Format with 1 decimal place for values like Beta
			return value.toFixed(1);
		default:
			return value.toString();
	}
};

dayjs.locale("en");
dayjs.tz.setDefault("America/New_York");
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(timezone);

const { current_hostname, env, backend_url } = useRuntimeConfig().public;
// Call songbird directly (e.g. https://songbird.stockstotrade.com/api/).
// SSR and CSR both hit the primary upstream from this page; resilience
// against 502/network failures is provided by the axios interceptor
// installed in server/plugins/songbird-fallback.ts (SSR) and
// plugins/songbird-fallback.client.ts (CSR), which transparently retry
// against SONGBIRD_FALLBACK_URL when the primary is unreachable.
const backend = backend_url + "api/";
const $router = useRoute();
const router = useRouter();

const isDev = env != "production";

// Real-time quote updates are pushed over Songbird's `/ws/stocks/quote`
// WebSocket (see stores/stocks.ts), which coexists with the REST endpoints
// and transparently falls back to REST-interval polling if the socket can't
// be established or drops repeatedly.
const stocksStore = useStocksStore();
const current_stock_data = ref<MostUpToDateDataType>();
const historic_stock_data = ref<HistoricDataType[]>([]);
const historicRangeMeta = ref<HistoricRangeMeta | null>(null);
const chartData = ref();
const isLoading = ref<boolean>(true);
const isLoadingNews = ref(true);
const selectedRange = ref();
const selectedChartType = ref<"Candlestick" | "Line Chart">("Candlestick");
const isRangeChanging = ref(false);

const financial_data = computed(() => {
	if (!company_data.value || !earnings_data.value) {
		return null;
	}

	try {
		const exchange = (company_data.value as any)?.rawData?.company?.[0]
			?.symbolinfo?.[0]?.key?.exLgName;

		if (exchange && exchange.includes("OTC")) {
			// OTC stocks handling
		}

		const revenue = (company_data.value as any)?.revenue;
		const sharesOutstanding = (company_data.value as any)?.rawData?.company?.[0]
			?.shareinfo?.shareinformation?.outstanding;
		const eps = (company_data.value as any)?.rawData?.company?.[0]?.shareinfo
			?.shareinformation?.eps;

		if (!revenue || !sharesOutstanding || !eps) {
			return null;
		}

		// Calculate net income
		const netIncome = eps * sharesOutstanding;

		// Calculate profit margin
		const profit_margin = (netIncome / revenue) * 100;

		// Handle earnings date - only available for rich data (Data A)
		let earnings_date = "N/A";
		if (Array.isArray(earnings_data.value) && earnings_data.value.length > 0) {
			const lastEarning = (earnings_data.value as any)[
				earnings_data.value.length - 1
			];
			earnings_date = lastEarning.reportDate?.[0] || "N/A";
		}

		return {
			pe_ratio: stock_data?.BID
				? Number(stock_data.BID / (company_data.value as any).eps).toFixed(2)
				: "N/A",
			earnings_date,
			eps: (company_data.value as any).eps || "N/A",
			revenue: (company_data.value as any).revenue
				? `$${((company_data.value as any).revenue / 1000000000).toFixed(2)}B`
				: "N/A",
			dividend_yield: (company_data.value as any).dividendYield
				? `${(company_data.value as any).dividendYield}%`
				: "N/A",
			profit_margin: `${profit_margin.toFixed(2)}%`
		};
	} catch (error) {
		console.error("Error calculating financial data:", error);
		return null;
	}
});

const formatHistoricNoticeDate = (value?: string | null) => {
	if (!value) {
		return "the most recent trading day";
	}

	const parsedDate = dayjs(value);
	if (!parsedDate.isValid()) {
		return value;
	}

	return parsedDate.format("MMMM D");
};

const previousTradingDayNotice = computed(() => {
	if (
		selectedRange.value !== "1D" ||
		!historicRangeMeta.value?.usedPreviousTradingDay
	) {
		return "";
	}

	return `Last day of market activity: ${formatHistoricNoticeDate(
		historicRangeMeta.value.displayTradingDate
	)}.`;
});

watch(
	() => historic_stock_data.value,
	(newData) => {
		// Validate data before updating chart
		if (newData && Array.isArray(newData) && newData.length > 0) {
			const stocks_data = newData.filter(
				(item) => item && item.open && item.high && item.low && item.close
			);

			// Check if data has required fields
			const isValidData = stocks_data.every(
				(item) =>
					item &&
					typeof item.open === "number" &&
					typeof item.high === "number" &&
					typeof item.low === "number" &&
					typeof item.close === "number" &&
					!isNaN(item.open) &&
					!isNaN(item.high) &&
					!isNaN(item.low) &&
					!isNaN(item.close)
			);

			if (isValidData) {
				chartData.value = getChartData(stocks_data);
			} else {
				console.warn("Invalid chart data detected, skipping update");
				// Log which items are invalid
				stocks_data.forEach((item, index) => {
					if (
						!item ||
						typeof item.open !== "number" ||
						typeof item.high !== "number" ||
						typeof item.low !== "number" ||
						typeof item.close !== "number" ||
						isNaN(item.open) ||
						isNaN(item.high) ||
						isNaN(item.low) ||
						isNaN(item.close)
					) {
						console.warn(`Invalid item at index ${index}:`, item);
					}
				});
			}
		} else {
			chartData.value = null;
		}
	}
);

function getChartData(historic_stock_data) {
	if (!historic_stock_data || !historic_stock_data.length) {
		return null;
	}

	const today = new Date();
	const daysMapper = {
		"1D": 1,
		"5D": 7, // Use 7 days to include weekends and show 5 business days
		"1M": 30,
		"6M": 180,
		"1Y": 365,
		"5Y": 1825,
		All: 10000
	};

	let filtered;

	if (selectedRange.value === "1D") {
		// For 1D range, don't filter by date and use 'time' field for labels
		filtered = historic_stock_data;
	} else {
		// For other ranges, filter by date and use 'date' field
		const pastDays = new Date(today);
		pastDays.setDate(today.getDate() - daysMapper[selectedRange.value || "1M"]);

		// Set time to start of day to include current day
		pastDays.setHours(0, 0, 0, 0);

		filtered = historic_stock_data
			.filter((d: any) => {
				const dataDate = new Date(d.time || d.date);
				// Include data from pastDays (inclusive) to today (inclusive)
				return dataDate >= pastDays && dataDate <= today;
			})
			.reverse();
	}

	return filtered;
}

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	BarElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	TimeScale,
	LineController,
	BarController,
	Filler
);

const buildStockInfoFallbackResponse = (
	statusCode = 500,
	message = "Server error at source"
) => {
	return {
		status: statusCode,
		data: {
			data: [],
			error: {
				hasError: true,
				statusCode,
				message
			}
		}
	};
};

// Cloudflare Workers terminate a request after ~30s of total time and around
// 60s wall-clock. Without an explicit axios timeout, a hung songbird upstream
// will hold the SSR open until the platform kills the worker, before our
// fallback interceptor (server/plugins/songbird-fallback.ts) ever sees an
// error to retry. We therefore set short timeouts on the server so the
// interceptor can retry against the Railway fallback well within the budget.
const SSR_AXIOS_TIMEOUT_MS = 6000;
const CSR_AXIOS_TIMEOUT_MS = 15000;
const axiosTimeout = () =>
	import.meta.server ? SSR_AXIOS_TIMEOUT_MS : CSR_AXIOS_TIMEOUT_MS;

const getStocksInfo = async ({ symbol }: { symbol: string }) => {
	const url = `${backend}stocks/quote?symbol=${symbol}`;

	try {
		const response = await axios.get(url, {
			timeout: axiosTimeout(),
			validateStatus: () => true
		});

		return response;
	} catch (error: any) {
		const fallbackStatus = error?.response?.status || 503;
		console.error(`Error fetching stock info for ${symbol}:`, error);
		console.error("Error details:", {
			message: error?.message,
			code: error?.code,
			stack: error?.stack,
			isServer: process.server,
			symbol
		});

		return buildStockInfoFallbackResponse(fallbackStatus);
	}
};

function daysAgoStock(date: string, isOnPage = false) {
	// Handle invalid or missing dates
	if (!date) {
		return "Unknown";
	}

	try {
		// Treat the input timestamp as EST (since it comes from EST server)
		const inputDateEST = dayjs.tz(date, "America/New_York");

		// Get current time in EST (regardless of user's system timezone)
		const nowEST = dayjs().tz("America/New_York");

		// Check if the date is valid
		if (!inputDateEST.isValid()) {
			console.warn("Invalid date provided to daysAgoStock:", date);
			return "Invalid date";
		}

		// Calculate difference in milliseconds (both times are in EST)
		let diff = nowEST.diff(inputDateEST);

		// Handle negative diff (when current time appears behind trade time due to timezone)
		if (diff < 0) {
			// If negative, it means we're in a timezone behind NY
			// Add 24 hours to get the correct time difference
			diff = Math.abs(diff);
			// If it's more than 12 hours negative, it's likely a day difference
			if (diff > 1000 * 60 * 60 * 12) {
				diff = 1000 * 60 * 60 * 24 - diff; // Adjust for day boundary
			}
		}

		if (isOnPage) {
			if (diff < 1000 * 60 * 60) {
				const minutes = Math.ceil(diff / 1000 / 60);
				return `${minutes} min${minutes >= 2 ? "s" : ""} ago`;
			} else if (diff < 1000 * 60 * 60 * 24) {
				const hours = Math.ceil(diff / 1000 / 60 / 60);
				return `${hours} hour${hours >= 2 ? "s" : ""} ago`;
			}
			const days = Math.ceil(diff / 1000 / 60 / 60 / 24);
			return `${days} day${days >= 2 ? "s" : ""} ago`;
		}

		if (diff < 1000 * 60) {
			const minutes = Math.ceil(diff / 1000 / 60);
			return `${minutes} min${minutes >= 2 ? "s" : ""} ago`;
		} else if (diff < 1000 * 60 * 60) {
			const hours = Math.ceil(diff / 1000 / 60 / 60);
			return `${hours} hour${hours >= 2 ? "s" : ""} ago`;
		} else {
			const days = Math.ceil(diff / 1000 / 60 / 60 / 24);
			return `${days} day${days >= 2 ? "s" : ""} ago`;
		}
	} catch (error) {
		console.error("Error in daysAgoStock function:", error, "Date:", date);
		return "Error";
	}
}

const STOCK_INFO_ASYNC_KEY = "stock_info";

const { data } = await useAsyncData(
	STOCK_INFO_ASYNC_KEY,
	async ({ $router, $config }) => {
		// Get symbol from route params with better validation
		const routeParams = $router.currentRoute._rawValue.params;
		const stockParam = routeParams?.stock;

		if (!stockParam || !Array.isArray(stockParam) || stockParam.length === 0) {
			console.error("Invalid route params:", routeParams);
			return null;
		}

		const symbol = stockParam[0]?.toUpperCase()?.trim();

		if (!symbol || symbol === "UNDEFINED" || symbol === "NULL") {
			console.error(
				"Invalid symbol from route:",
				symbol,
				"Route params:",
				routeParams
			);
			return null;
		}

		const buildErrorResult = (errorPayload?: {
			statusCode?: number;
			message?: string;
			hasError?: boolean;
		}) => {
			const statusCode = errorPayload?.statusCode ?? 500;

			return {
				stock_data: null,
				symbol,
				stock_data_response: null,
				error: {
					hasError: true,
					statusCode,
					message:
						errorPayload?.message ||
						(statusCode >= 500
							? "Server error at source"
							: "Unable to load stock data")
				}
			};
		};

		try {
			const response = await getStocksInfo({
				symbol
			});

			const { data: responseData } = response;

			// API signaled an error or returned a failing status
			if (response.status !== 200 || responseData?.error?.hasError) {
				console.warn(
					"API responded with an error payload:",
					responseData?.error || response.status
				);
				return buildErrorResult(
					responseData?.error || {
						statusCode: response.status,
						message: responseData?.error?.message
					}
				);
			}

			// Check if responseData exists and has the expected structure
			if (
				!responseData ||
				!responseData.data ||
				!Array.isArray(responseData.data) ||
				responseData.data.length === 0
			) {
				console.error("Invalid data structure:", responseData);
				return buildErrorResult({
					statusCode: response.status,
					message: "Empty response from stock data API"
				});
			}

			const stockData = responseData.data[0];

			// Relaxed validation for OTC stocks - only require SYMBOL
			if (!stockData || !stockData.SYMBOL) {
				console.error("Invalid stock data structure - missing SYMBOL:", stockData);
				return null;
			}

			// Extract data with defaults for OTC stocks
			const {
				BID = null,
				NAME = symbol, // Use symbol as fallback name
				NET_CHG = 0,
				PC_CHG = 0,
				HIGH_52W = null,
				LOW_52W = null,
				EPS = null,
				PE_RATIO = null,
				TIME = null,
				HIGH = null,
				LOW = null
			} = stockData;

			const stock_data = {
				BID,
				NAME,
				SYMBOL: stockData.SYMBOL || symbol,
				TIME,
				NET_CHG,
				PC_CHG,
				HIGH_52W,
				LOW_52W,
				EPS,
				PE_RATIO,
				HIGH,
				LOW,
				LAST_TRADE_TIME: TIME,
				...stockData
			};

			let stock_data_response = null;
			try {
				const response = await axios.get(`${backend}stocks/fundamentals`, {
					params: {
						symbol: stock_data.SYMBOL,
						dataType:
							"company,earnings,earnings_dates,analyst,enhanced_financials,broker_ratings,dividends"
					},
					// Same SSR timeout reasoning as getStocksInfo above. Fundamentals
					// data is best-effort: failure here is silently swallowed below,
					// so we'd rather give up early than burn the worker's time budget.
					timeout: axiosTimeout()
				});

				if (response.status === 200) {
					stock_data_response = response.data;
				}
			} catch (error) {
				console.warn("📊 /api/stock-data failed for:", stock_data.SYMBOL, error);
				// Continue without stock_data_response for OTC stocks
			}

			return { stock_data, symbol, stock_data_response };
		} catch (error) {
			console.error("Initial stock data fetch failed", error);
			return buildErrorResult({
				statusCode: 500,
				message: "Server error at source"
			});
		}
	},
	{
		server: true,
		lazy: false,
		default: () => null
	}
);

// Don't throw 404 immediately - let the component handle it gracefully
if (data.value === null) {
	throw createError({
		statusCode: 404,
		statusMessage: "Stock symbol not found"
	});
}

// Safely destructure stock_data with null checks
const stock_data = data.value?.stock_data || null;
const stock_data_realtime = ref<any>(stock_data);
const stock_response = data.value?.stock_data_response || null;
const stockDataError = ref(data.value?.error || null);

// CRITICAL: when SSR couldn't load real stock data, do NOT let Cloudflare cache
// the resulting "We're having trouble loading X" template. The route rule for
// /quote/** sets `s-maxage=600`, which would otherwise pin a broken page at
// the edge for 10 minutes (with `stale-if-error=86400` extending it further).
// Override the headers from the page so the failure is served once, never
// cached, and the next direct hit goes back to origin and can succeed.
if (import.meta.server && (!stock_data || stockDataError.value)) {
	const event = useRequestEvent();
	if (event) {
		setResponseHeader(
			event,
			"Cache-Control",
			"private, no-store, no-cache, must-revalidate"
		);
		setResponseHeader(event, "CDN-Cache-Control", "no-store");
		setResponseHeader(event, "Cloudflare-CDN-Cache-Control", "no-store");
	}
}

const stockDataErrorMessage = computed(() => {
	const error = stockDataError.value;

	if (!error) {
		return "We couldn’t load this quote right now. Please try again shortly.";
	}

	if (error.statusCode >= 500) {
		return "We’re experiencing a small technical problem. Please try again in a few minutes.";
	}

	// if (error.statusCode === 404) {
	// 	return "We can’t seem to find that ticker. Double-check the symbol or try again later.";
	// }

	return "We couldn’t load this quote right now. Please try again later.";
});

const retryInitialFetch = () => {
	if (process.client) {
		window.location.reload();
	}
};

const fallbackDisplaySymbol = computed(() => {
	const routeSymbol =
		(Array.isArray($router.params?.stock) && $router.params.stock[0]) || "";
	const formattedRouteSymbol = routeSymbol ? routeSymbol.toUpperCase() : "";

	return data.value?.symbol || formattedRouteSymbol || "this ticker";
});

// Set loading state based on data availability
watch(
	data,
	(newData) => {
		isLoading.value = !newData;
		stockDataError.value = newData?.error || null;
	},
	{ immediate: true }
);

function setRange(range: any) {
	selectedRange.value = range;

	if (env === "dev") {
		router.push({
			query: {
				...$router.query,
				range
			}
		});
	}
	// Fetch or filter your chart data here
}

function setChartType(type: any) {
	selectedChartType.value = type;
}

let historicDataController: AbortController | null = null;

const getHistoricFieldsForRange = (range: string) => {
	if (range === "1D" || range === "5D") {
		return "time,open,high,low,close,volume";
	}

	return "date,open,high,low,close,avolume,sharevolume";
};

const fetchHistoricData = async (symbol: any, range: any) => {
	historicDataController?.abort();
	historicDataController = new AbortController();

	const response = await axios.get(`${backend}stocks/history`, {
		timeout: axiosTimeout(),
		signal: historicDataController.signal,
		params: {
			symbol,
			range,
			fields: getHistoricFieldsForRange(range)
		}
	});

	if (response.status === 200 && response.data) {
		const data = response.data.results?.[symbol];
		historicRangeMeta.value = response.data.meta?.[symbol] || null;
		if (!data) return;

		// IN CASE SOMETHING BREAKS TMW, REMOVE THIS BLOCK OF CODE
		if (range !== "1D" && range !== "5D") {
			const historicData = data;
			const lastHistoricItem = historicData[historicData.length - 1];
			const currentTradeTime = stock_data_realtime.value?.LAST_TRADE_TIME;

			if (!lastHistoricItem || lastHistoricItem.date !== currentTradeTime) {
				const latest_item = {
					changepercent: stock_data_realtime.value?.PC_CHG || 0,
					change: stock_data_realtime.value?.NET_CHG || 0,
					open:
						stock_data_realtime.value?.LAST || stock_data_realtime.value?.OPEN || 0,
					close:
						stock_data_realtime.value?.LAST ||
						stock_data_realtime.value?.BID ||
						stock_data_realtime.value?.CLOSE ||
						0,
					high:
						stock_data_realtime.value?.HIGH || stock_data_realtime.value?.CLOSE || 0,
					low:
						stock_data_realtime.value?.LOW || stock_data_realtime.value?.CLOSE || 0,
					date:
						stock_data_realtime.value?.LAST_TRADE_TIME ||
						new Date().toISOString().split("T")[0],
					avolume: stock_data_realtime.value?.VOLUME || 0,
					sharevolume: stock_data_realtime.value?.VOLUME || 0
				};

				// Add the latest item to the beginning of historic data
				historic_stock_data.value = [latest_item, ...historicData];
			}
		} else {
			historic_stock_data.value = data;
		}
		// END OF CASE SOMETHING BREAKS TMW, REMOVE THIS BLOCK OF CODE

		// uncomment this line to see the historic data
		// historic_stock_data.value = data;
	} else {
		historicRangeMeta.value = null;
	}
};
watch(
	selectedRange,
	async (new_range, old_range) => {
		if (import.meta.client && new_range !== old_range) {
			// Set range changing flag to prevent real-time updates during transition
			// isRangeChanging.value = true;

			const symbol = $router.params.stock[0];

			try {
				await fetchHistoricData(symbol, new_range);
			} catch (err: any) {
				if (axios.isCancel(err) || err?.code === "ERR_CANCELED") return;
				throw err;
			} finally {
				// Clear range changing flag after a short delay to allow chart to stabilize
				// setTimeout(() => {
				// isRangeChanging.value = false;
				// }, 1000); // 1 second delay
			}
		}
	},
	{
		deep: true
	}
);

const earnings_data = ref([]);
const company_data = ref(null);
const analyst_data = ref(null);
const enhanced_financials_data = ref(null);
const earnings_dates_data = ref(null);
const ratings_data = ref(null);
const related_news = ref<any>([]);
const stock_earnings_faq_data = ref<StockEarningsFAQResponse | null>(null);
const dividends_data = ref(null);
const isOverviewExpanded = ref(false);
const chartContainer = ref<HTMLElement>();
const isScrollAtEnd = ref(false);
const isClient = ref(false);
// Function to toggle overview expansion
const toggleOverviewExpansion = () => {
	isOverviewExpanded.value = !isOverviewExpanded.value;
};

// Handle chart container scroll
const handleChartScroll = () => {
	if (!chartContainer.value) {
		return;
	}

	const { scrollLeft, scrollWidth, clientWidth } = chartContainer.value;
	const scrollEnd = scrollWidth - clientWidth;

	// Consider "at end" if within 5px of the end
	isScrollAtEnd.value = scrollLeft >= scrollEnd - 5;
};

// Computed properties for analyst ratings (QuoteMedia format)
const analystRatingLabel = computed(() => {
	const data = analyst_data.value as any;
	if (!data || !data.meanRecommend) {
		return "N/A";
	}

	const meanRating = data.meanRecommend.current;
	if (typeof meanRating !== "number") {
		return "Hold";
	}

	// QuoteMedia scale: 1=Strong Buy, 2=Buy, 3=Hold, 4=Sell, 5=Strong Sell
	if (meanRating <= 1.5) {
		return "Strong Buy";
	}
	if (meanRating <= 2.5) {
		return "Buy";
	}
	if (meanRating <= 3.5) {
		return "Hold";
	}
	if (meanRating <= 4.5) {
		return "Sell";
	}
	return "Strong Sell";
});

const analystPriceTarget = computed(() => {
	const data = ratings_data.value as any;
	if (!data) {
		return 0;
	}

	// QuoteMedia doesn't seem to include price target in this response
	// You might need a separate endpoint for price targets
	return (data as any).meantargetprice || 0;
});

const analystTotalRatings = computed(() => {
	const data = analyst_data.value as any;
	if (!data || !data.meanRecommend) {
		return 0;
	}

	return data.meanRecommend.numReporting || 0;
});

const analystBuyCount = computed(() => {
	const data = analyst_data.value as any;
	if (!data) {
		return 0;
	}

	// Combine Strong Buy + Moderate Buy
	const strongBuy = data.strongBuy?.current || 0;
	const moderateBuy = data.moderateBuy?.current || 0;
	return strongBuy + moderateBuy;
});

const analystHoldCount = computed(() => {
	const data = analyst_data.value as any;
	if (!data) {
		return 0;
	}

	return data.hold?.current || 0;
});

const analystSellCount = computed(() => {
	const data = analyst_data.value as any;
	if (!data) {
		return 0;
	}

	// Combine Strong Sell + Moderate Sell
	const strongSell = data.strongSell?.current || 0;
	const moderateSell = data.moderateSell?.current || 0;
	return strongSell + moderateSell;
});

// Computed property using Day.js
const formattedLastTradeTime = computed(() => {
	if (!stock_data_realtime.value?.LAST_TRADE_TIME) {
		return "";
	}

	try {
		const date = dayjs(stock_data_realtime.value.LAST_TRADE_TIME);

		// Check if date is valid
		if (!date.isValid()) {
			return stock_data_realtime.value.LAST_TRADE_TIME;
		}

		// Format to "Jun 12 2025 7:45AM EDT"
		return date.format("MMM D YYYY h:mmA [EST]");
	} catch (error) {
		console.error("Error formatting date:", error);
		return stock_data_realtime.value.LAST_TRADE_TIME;
	}
});

const exchange_parser = (exchange: string) => {
	// Handle undefined, null, or empty exchange values
	if (!exchange || typeof exchange !== "string") {
		return "Stock Exchange";
	}

	if (
		exchange === "NASDAQ" ||
		exchange === "NASDAQ-NMS" ||
		exchange === "NGS" ||
		exchange === "NSC" ||
		exchange === "NSD" ||
		exchange === "NASDAQ-NMS-ARCA"
	) {
		return "NASDAQ";
	}
	if (
		exchange === "NYE" ||
		exchange === "NYSE" ||
		exchange === "NYSE-ARCA" ||
		exchange === "NYSE-ARCA-ARCA"
	) {
		return "NYSE";
	}
	if (
		exchange === "AMX" ||
		exchange === "AMEX" ||
		exchange === "AMEX-ARCA" ||
		exchange === "AMEX-ARCA-ARCA"
	) {
		return "AMEX";
	}
	return exchange;
};
// Computed property for normalized exchange from company data
const normalizedExchange = computed(() => {
	if (
		!(company_data.value as any)?.rawData?.company?.[0]?.symbolinfo?.[0]?.key
			?.exchange
	) {
		return "";
	}

	const exchange = (company_data.value as any)?.rawData?.company?.[0]
		?.symbolinfo?.[0]?.key?.exchange;

	return getExchangeDisplayName(normalizeExchange(exchange));
});

onMounted(async () => {
	if (stock_data?.SYMBOL) {
		fetch(getSongbirdOgImageUrl(stock_data.SYMBOL), { redirect: "follow" }).catch(
			() => {}
		);
	}

	if (import.meta.client) {
		// Trigger OG image refresh in the background (fire-and-forget).

		// Reload page on orientation change (mobile rotation)
		const handleOrientationChange = () => {
			// Small delay to ensure orientation change is complete
			setTimeout(() => {
				window.location.reload();
			}, 100);
		};

		// Add orientation change listener
		window.addEventListener("orientationchange", handleOrientationChange);

		// Cleanup listener on component unmount
		onUnmounted(() => {
			window.removeEventListener("orientationchange", handleOrientationChange);
			// Stop the WS/REST-fallback quote stream for this symbol
			stocksStore.stopTrackingSymbols();
		});

		const range_query = $router.query.range as string;

		if (range_query && range_query.length > 0 && env === "dev") {
			selectedRange.value = range_query.toUpperCase();
		} else {
			selectedRange.value = "1D";
		}

		// Initialize scroll state
		nextTick(() => {
			handleChartScroll();
		});

		isClient.value = true;

		if (stock_response) {
			// Extract earnings data - handle two different data structures
			if ((stock_response as any).earnings) {
				const earningsData = (stock_response as any).earnings;

				// Data A: earnings is direct array (rich stocks like VWAGY)
				if (Array.isArray(earningsData)) {
					earnings_data.value = earningsData.reverse() as any[];
				}
				// Data B: earnings.results.symbolEarnings (OTC stocks like APPL)
				else if (
					earningsData.results?.symbolEarnings &&
					earningsData.results?.symbolEarnings.length > 0 &&
					earningsData.results?.symbolEarnings[0]?.earning
				) {
					earnings_data.value =
						earningsData.results?.symbolEarnings[0]?.earning
							?.slice(0, 6)
							?.reverse() || []; // Empty array for OTC stocks with no earnings
				} else {
					earnings_data.value = [];
				}
			} else {
				earnings_data.value = []; // Default empty array
			}

			// Extract company data
			if ((stock_response as any).company) {
				company_data.value = (stock_response as any).company;
			}

			// Extract analyst data
			if ((stock_response as any).analyst) {
				analyst_data.value = (stock_response as any).analyst.results.analyst;
			}

			// Extract enhanced financials data
			if ((stock_response as any).enhanced_financials) {
				enhanced_financials_data.value = (
					stock_response as any
				).enhanced_financials;
			}
			// Extract earnings dates data
			if ((stock_response as any).earnings_dates) {
				earnings_dates_data.value = (stock_response as any).earnings_dates;
			}
			// Extract earnings dates data
			if ((stock_response as any).broker_ratings) {
				ratings_data.value = (stock_response as any).broker_ratings;
			}
			// Extract earnings dates data
			if ((stock_response as any).dividends) {
				dividends_data.value = (stock_response as any).dividends;
			}
			// Extract earnings dates data
			if ((stock_response as any).broker_ratings) {
				ratings_data.value = (stock_response as any).broker_ratings;
			}

			// Note: stock_earnings_faq_response will be fetched in parallel below
		} else {
			// Set defaults for OTC stocks without additional data
			earnings_data.value = [];
			company_data.value = null;
			analyst_data.value = null;
			enhanced_financials_data.value = null;
			earnings_dates_data.value = null;
			ratings_data.value = null;
			dividends_data.value = null;
		}

		const symbol = $router.params.stock[0];

		// Run both API calls in parallel for better performance
		let related_news_response = null;
		// Tell Songbird which product is asking so it returns Timothy Sykes news
		// (origin "ts") instead of the default StocksToTrade feed. The shared
		// backend lives on a *.stockstotrade.com host, so the Host header always
		// looks like STT — X-Original-Origin is the only reliable product signal.
		const response = await axios.get(`${backend}stocks/news`, {
			timeout: axiosTimeout(),
			headers: {
				"X-Original-Origin": current_hostname
			},
			params: {
				symbol
			}
		});

		if (response.status === 200) {
			related_news_response = response.data;
		}

		// Always set related_news to ensure reactivity works properly
		if (related_news_response && Array.isArray(related_news_response)) {
			related_news.value = related_news_response.map((item: any) => ({
				...item,
				date: dayjs(item.date).format("MMM D, YYYY")
			}));
		} else {
			// Set to empty array if no valid response
			related_news.value = [];
		}

		isLoadingNews.value = false;

		if (data.value) {
			isLoading.value = false;

			stock_data_realtime.value = stock_data;
			current_stock_data.value = stock_data;

			const symbol = data.value.symbol;

			// Start live quote updates for this symbol over the Songbird
			// WebSocket (falls back to REST-interval polling automatically —
			// see stores/stocks.ts).
			await stocksStore.startTrackingSymbols([symbol]);
		}
	}
});

// Merge pushed quote updates (WS or REST fallback, both flow through the
// store) into the realtime ref the template reads from.
watch(
	() => (data.value?.symbol ? stocksStore.stocks[data.value.symbol] : null),
	(newStock) => {
		// Skip while the chart range is transitioning, mirroring the previous
		// polling behavior.
		if (!newStock || isRangeChanging.value) return;

		stock_data_realtime.value = {
			...stock_data_realtime.value,
			...newStock,
			LAST_TRADE_TIME:
				newStock.TIME ?? stock_data_realtime.value?.LAST_TRADE_TIME ?? null
		};
	},
	{ deep: true }
);

// SEO Configuration with useHead
// useHead must be synchronous — async getters are not supported by Unhead and
// cause meta tags to be missing from the SSR HTML (crawlers never see them).
// The og:image URL points directly at the public S3 object (ts-og-images/),
// mirroring how StocksToTrade references its stt-og-images bucket. This is fast
// for crawlers and avoids a backend round-trip. The image is kept warm by
// Songbird (server-side generation on the quote path + the serve-endpoint
// trigger fired in onMounted below).
useHead(() => {
	if (!stock_data) {
		return {
			title: "Stock Quote | StocksToTrade",
			meta: [
				{
					name: "description",
					content:
						"Get the latest stock prices, news, and charts. Access real-time quotes and historical data with interactive charts and tools to make informed trading decisions."
				}
			]
		};
	}

	const companyName =
		stock_data.NAME && stock_data.NAME !== "" && stock_data.NAME !== "null"
			? stock_data.NAME
			: stock_data.SYMBOL;

	const symbol = stock_data.SYMBOL || "";

	// Safely get exchange information with fallback
	const exchange =
		stock_response?.company?.rawData?.company?.[0]?.symbolinfo?.[0]?.key
			?.exchange ||
		stock_data?.MARKET ||
		"Stock Exchange";

	const pageTitle = `${companyName} (${symbol}) Stock Price | Live Quotes & Charts | ${exchange_parser(
		exchange
	)}`;
	const metaDescription = `Get latest ${companyName} (${symbol}) stock price, news, and charts. Access real-time quotes and historical data with interactive charts and tools to make informed trading decisions.`;

	// Safely construct URLs with proper validation
	const baseUrl = current_hostname?.slice(0, -1) || "";
	const canonicalUrl = `${baseUrl}/quote/${symbol}/`;

	const ogImageUrl = getTsOgImageS3Url(symbol || "DEFAULT");

	return {
		title: pageTitle,
		link: [
			{
				rel: "preconnect",
				href: "https://stt-ts-resources.s3.us-east-1.amazonaws.com"
			},
			{
				rel: "preconnect",
				href: "https://content.stockstotrade.com/"
			},
			{
				rel: "canonical",
				href: canonicalUrl
			}
		],
		meta: [
			{
				name: "description",
				content: metaDescription
			},
			{
				property: "og:title",
				content: pageTitle
			},
			{
				property: "og:description",
				content: metaDescription
			},
			{
				property: "og:url",
				content: canonicalUrl
			},
			{
				property: "og:image",
				content: ogImageUrl
			},
			{
				property: "og:image:width",
				content: "1200"
			},
			{
				property: "og:image:height",
				content: "675"
			},
			{
				property: "og:type",
				content: "website"
			},
			{
				property: "article:published_time",
				content: stock_data.TIME || new Date().toISOString()
			}
		]
	};
});
const breakpoints = useBreakpoints({
	lg: 1280,
	mini: 1024,
	xl: 1440
});

const isMini = computed(() => breakpoints.isGreaterOrEqual("mini"));
const isXl = computed(() => breakpoints.isGreater("xl"));
const isDesktop = computed(() => breakpoints.isGreaterOrEqual("lg"));

const isAmericanExchange = computed(() => {
	if (stock_data && stock_data.MARKET) {
		if (
			stock_data.MARKET === "NYSE" ||
			stock_data.MARKET === "NYE" ||
			stock_data.MARKET === "NASDAQ" ||
			stock_data.MARKET === "AMEX" ||
			stock_data.MARKET === "AMEX-ARCA" ||
			stock_data.MARKET === "AMEX-ARCA-ARCA" ||
			stock_data.MARKET === "NASDAQ-NMS" ||
			stock_data.MARKET === "NASDAQ-NMS-ARCA" ||
			stock_data.MARKET === "NGS" ||
			stock_data.MARKET === "NSC"
		) {
			return true;
		}
	}
	return false;
});

// Stop the quote WebSocket/fallback stream when navigating away from the route
onBeforeRouteLeave((_to, _from, next) => {
	stocksStore.stopTrackingSymbols();
	next();
});

const stock_sector = computed(() => {
	const data: any = company_data.value;

	if (!data || !data.rawData) {
		return null;
	}

	return data.rawData?.company[0]?.profile?.classification?.sector || null;
});
</script>

<style lang="scss">
@use "~/assets/utils" as *;

body {
	color: $slate-900 !important;
}

.chart-controls {
	@apply inline-flex gap-2 px-1 py-1  rounded-lg;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	-webkit-overflow-scrolling: touch;
	position: relative;
	background-color: #eceff3;

	&::-webkit-scrollbar {
		display: none;
	}

	scrollbar-width: none;

	width: fit-content;
	margin-bottom: 8px;

	@include breakpoint(lg) {
		margin-bottom: 16px;
	}
}

.chart-controls-container {
	position: relative;
	@apply rounded-xl;
	overflow: hidden;

	&::before {
		content: "";
		display: block;
		width: 72px;
		height: 100%;
		background: linear-gradient(to right, rgba(#dfe1e7, 0), rgba(#f1f2f5, 1));
		position: absolute;
		right: 0;
		top: 0;
		z-index: 1000;
		pointer-events: none;

		@include breakpoint(lg) {
			display: none;
		}
	}
}

.quote-body {
	gap: 16px !important;
	display: flex;
	flex-direction: column;

	@include breakpoint(lg) {
		gap: 24px !important;
	}
}

.right-overlay {
	position: absolute;
	right: 0; /* Account for pr-8 (32px) */
	top: 0;
	width: 96px;
	pointer-events: none;
	height: 350px; /* Match chart height */
	background: linear-gradient(to right, rgba(#f6f8fa, 0), rgba(#f6f8fa, 1));
	z-index: 9;
	transition: opacity 0.2s ease;

	@include breakpoint(md) {
		display: none;
	}
}

.gap-4 {
	gap: 16px !important;
}
/* Additional styles if needed */
</style>
